/**
 * DetailTriageBreakdown — the per-triage-code snapshot on a hospital's detail
 * screen, sitting right under the hero total so a visitor can see *who* is
 * waiting (Rosso…Blu), not just how many.
 *
 * The detail page's own queries (history/estimated-wait) can't give an exact,
 * honest per-colour count — history is averaged/gap-filled and the wait figures
 * are cumulative — so the breakdown is fed the live `/hospitals` overview state
 * for this slug (exact integers, the same numbers the overview cards show). It
 * reuses {@link TriageMix}; colour is never the sole channel (every level keeps
 * its Italian label + count) and nothing pulses or alarms (DESIGN.md: triage
 * hues are quarantined, no dark-pattern urgency).
 *
 * While the overview is still loading it renders a height-matched placeholder
 * rather than `return null`: this block sits above the fold, so popping it in a
 * frame later would shift the panels below (CLS) and trip browser scroll
 * anchoring (AGENTS.md "reserve layout footprint / never return null while
 * loading"). Once settled with no live reading (hasData:false, unknown slug, or
 * a failed overview fetch) it is legitimately absent — the hero already carries
 * the no-data message, and a row of zeros would read as a false "all clear".
 */

import type { HospitalQueueState } from "@/api/generated/model";
import { TriageMix } from "./TriageMix";

interface DetailTriageBreakdownProps {
  /** This hospital's live queue state from the overview feed, or `undefined` until it arrives. */
  state: HospitalQueueState | undefined;
  /** Whether the live overview query is still pending (no cached data yet). */
  isPending: boolean;
}

export function DetailTriageBreakdown({ state, isPending }: DetailTriageBreakdownProps) {
  if (!state) {
    // No state yet: reserve the footprint while loading; stay quiet once settled
    // (unknown slug / failed fetch) — the hero carries the primary state.
    if (!isPending) return null;
    // Placeholder that reserves the settled block's exact footprint: the real
    // (static) heading + a .triage-mix-shaped bar/legend skeleton, so the panels
    // below don't shift when the live data lands (CLS-measured to ~0).
    return (
      <section className="detail-breakdown detail-breakdown--loading" aria-hidden="true">
        <h2 className="detail-breakdown__title">In attesa per codice triage</h2>
        <div className="triage-mix">
          <div className="detail-breakdown__bar-skeleton skeleton-block" />
          <div className="detail-breakdown__legend-skeleton skeleton-block" />
        </div>
      </section>
    );
  }

  // Settled, but this hospital has no live reading (just seeded / mid-outage):
  // the hero already shows the no-data message.
  if (!state.hasData) return null;

  return (
    <section className="detail-breakdown" aria-labelledby="detail-breakdown-title">
      <h2 id="detail-breakdown-title" className="detail-breakdown__title">
        In attesa per codice triage
      </h2>
      <TriageMix triage={state.triage} total={state.totalWaiting} />
    </section>
  );
}
